import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ExitPipelineCard from './components/ExitPipelineCard';
import ExitWorkflowPanel from './components/ExitWorkflowPanel';
import FinalPayCalculator from './components/FinalPayCalculator';
import KnowledgeTransferPortal from './components/KnowledgeTransferPortal';
import ExitInterviewModule from './components/ExitInterviewModule';
import BiometricSecurityDisclaimer from '../../components/navigation/BiometricSecurityDisclaimer';

const EmployeeOffboardingExitManagement = () => {
  const [activeTab, setActiveTab] = useState('active-departures');
  const [departures, setDepartures] = useState([]);
  const [selectedDeparture, setSelectedDeparture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);
  const [showKnowledgeTransfer, setShowKnowledgeTransfer] = useState(false);
  const [showExitInterview, setShowExitInterview] = useState(false);

  const handleFilterChange = (filters) => {
    // Handle global filter changes
    console.log('Filters changed:', filters);
  };

  useEffect(() => {
    loadOffboardingData();
  }, [activeTab]);

  const loadOffboardingData = async () => {
    try {
      setLoading(true);
      setError('');

      // Mock data for departing employees
      const mockDepartures = [
        {
          id: 1,
          employeeName: 'David Kila',
          position: 'Senior HR Manager',
          department: 'Human Resources',
          lastWorkingDay: '2026-02-15',
          terminationType: 'resignation',
          completionRate: 65,
          status: 'in-progress',
          tasksCompleted: 13,
          tasksTotal: 20,
          assetsReturned: 2,
          assetsTotal: 5,
          finalPayStatus: 'pending'
        },
        {
          id: 2,
          employeeName: 'Grace Moi',
          position: 'Compliance Analyst',
          department: 'Compliance',
          lastWorkingDay: '2026-02-28',
          terminationType: 'contract-end',
          completionRate: 40,
          status: 'in-progress',
          tasksCompleted: 8,
          tasksTotal: 20,
          assetsReturned: 1,
          assetsTotal: 4,
          finalPayStatus: 'pending'
        },
        {
          id: 3,
          employeeName: 'James Wari',
          position: 'IT Administrator',
          department: 'Information Technology',
          lastWorkingDay: '2026-01-31',
          terminationType: 'resignation',
          completionRate: 85,
          status: 'in-progress',
          tasksCompleted: 17,
          tasksTotal: 20,
          assetsReturned: 4,
          assetsTotal: 6,
          finalPayStatus: 'calculated'
        }
      ];

      setDepartures(mockDepartures);
    } catch (err) {
      setError(err?.message || 'Failed to load offboarding data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (departure) => {
    setSelectedDeparture(departure);
  };

  const handleFinalPayCalculation = (departure) => {
    setSelectedDeparture(departure);
    setShowCalculator(true);
  };

  const handleKnowledgeTransfer = (departure) => {
    setSelectedDeparture(departure);
    setShowKnowledgeTransfer(true);
  };

  const handleExitInterview = (departure) => {
    setSelectedDeparture(departure);
    setShowExitInterview(true);
  };

  const tabs = [
    { id: 'active-departures', label: 'Active Departures', icon: 'Users', count: 3 },
    { id: 'completed', label: 'Completed', icon: 'CheckCircle', count: 0 }
  ];

  const pipelineStats = {
    activeDepartures: 3,
    averageCompletion: 63,
    criticalDeadlines: 1,
    pendingFinalPay: 2,
    assetsOutstanding: 8
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <Header />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <BiometricSecurityDisclaimer />

      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-amber-600 to-amber-700 p-3 rounded-xl shadow-lg">
                <Icon name="UserMinus" size={32} className="text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">
                  Employee Offboarding & Exit Management
                </h1>
                <p className="text-gray-600 mt-1">
                  Structured departure workflows with asset recovery and PNG Employment Act compliance
                </p>
              </div>
            </div>
            <Button
              onClick={() => alert('Initiate Offboarding')}
              className="bg-gradient-to-r from-amber-600 to-amber-700 text-white"
            >
              <Icon name="Plus" size={20} className="mr-2" />
              Initiate Offboarding
            </Button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Exit Pipeline Dashboard - 4 cols */}
          <div className="col-span-12 lg:col-span-4">
            <ExitPipelineCard stats={pipelineStats} />
          </div>

          {/* Main Management Panel - 8 cols */}
          <div className="col-span-12 lg:col-span-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200">
              {/* Tabbed Interface */}
              <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50' :'text-gray-600 hover:text-amber-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={20} />
                      {tab?.label}
                      {tab?.count > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          {tab?.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Icon name="Loader" size={32} className="animate-spin text-amber-600" />
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error}
                  </div>
                ) : departures?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No active departures</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {departures?.map(departure => (
                      <div key={departure?.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-1">
                              {departure?.employeeName}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {departure?.position} • {departure?.department}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <p className="text-xs text-gray-500">
                                Last Day: {new Date(departure?.lastWorkingDay)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </p>
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                {departure?.terminationType?.replace('-', ' ')?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleViewDetails(departure)}
                              variant="outline"
                              size="sm"
                            >
                              <Icon name="Eye" size={16} className="mr-1" />
                              View
                            </Button>
                            <Button
                              onClick={() => handleFinalPayCalculation(departure)}
                              variant="outline"
                              size="sm"
                            >
                              <Icon name="DollarSign" size={16} className="mr-1" />
                              Final Pay
                            </Button>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-700">
                              Exit Workflow Progress
                            </span>
                            <span className="text-xs font-bold text-amber-600">
                              {departure?.completionRate}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                departure?.completionRate >= 90
                                  ? 'bg-green-500'
                                  : departure?.completionRate >= 50
                                  ? 'bg-amber-500' :'bg-red-500'
                              }`}
                              style={{ width: `${departure?.completionRate}%` }}
                            />
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-600 mb-1">Tasks</p>
                            <p className="text-sm font-bold text-gray-900">
                              {departure?.tasksCompleted} / {departure?.tasksTotal}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-600 mb-1">Assets</p>
                            <p className="text-sm font-bold text-gray-900">
                              {departure?.assetsReturned} / {departure?.assetsTotal}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-600 mb-1">Final Pay</p>
                            <p className="text-sm font-bold text-gray-900">
                              {departure?.finalPayStatus?.toUpperCase()}
                            </p>
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                          <Button
                            onClick={() => handleKnowledgeTransfer(departure)}
                            size="sm"
                            variant="outline"
                          >
                            <Icon name="BookOpen" size={14} className="mr-1" />
                            Knowledge Transfer
                          </Button>
                          <Button
                            onClick={() => handleExitInterview(departure)}
                            size="sm"
                            variant="outline"
                          >
                            <Icon name="MessageSquare" size={14} className="mr-1" />
                            Exit Interview
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Exit Workflow Panel */}
        {selectedDeparture && !showCalculator && !showKnowledgeTransfer && !showExitInterview && (
          <div className="mt-6">
            <ExitWorkflowPanel departure={selectedDeparture} onClose={() => setSelectedDeparture(null)} />
          </div>
        )}

        {/* Final Pay Calculator */}
        {showCalculator && selectedDeparture && (
          <div className="mt-6">
            <FinalPayCalculator
              departure={selectedDeparture}
              onClose={() => {
                setShowCalculator(false);
                setSelectedDeparture(null);
              }}
            />
          </div>
        )}

        {/* Knowledge Transfer Portal */}
        {showKnowledgeTransfer && selectedDeparture && (
          <div className="mt-6">
            <KnowledgeTransferPortal
              departure={selectedDeparture}
              onClose={() => {
                setShowKnowledgeTransfer(false);
                setSelectedDeparture(null);
              }}
            />
          </div>
        )}

        {/* Exit Interview Module */}
        {showExitInterview && selectedDeparture && (
          <div className="mt-6">
            <ExitInterviewModule
              departure={selectedDeparture}
              onClose={() => {
                setShowExitInterview(false);
                setSelectedDeparture(null);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeOffboardingExitManagement;